
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { PersonalInfoForm } from './PersonalInfoForm';
import { useTeacherProfile } from '@/hooks/useTeacherProfile';
import { CreateTeacherProfileData, UpdateTeacherProfileData } from '@/types/teacherProfile';

interface PersonalInfoStepProps {
  teacherCode: string;
  onComplete: () => void;
}

export function PersonalInfoStep({ teacherCode, onComplete }: PersonalInfoStepProps) {
  const [stepConfirmed, setStepConfirmed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [personalInfoData, setPersonalInfoData] = useState<any>(null);
  const [isPersonalInfoValid, setIsPersonalInfoValid] = useState(false);

  const { profile, createProfile, updateProfile } = useTeacherProfile(teacherCode);

  const handlePersonalInfoDataChange = (data: any, isValid: boolean) => {
    setPersonalInfoData(data);
    setIsPersonalInfoValid(isValid);
  };

  const handleComplete = async () => {
    if (stepConfirmed && isPersonalInfoValid && personalInfoData) {
      setIsSubmitting(true);
      
      let success = false;

      if (profile) {
        // Update existing profile
        const updateData: UpdateTeacherProfileData = {
          full_name: personalInfoData.full_name,
          nickname: personalInfoData.nickname,
          phone_number: personalInfoData.phone_number,
          teaching_experience: personalInfoData.teaching_experience || undefined,
          academic_achievements: personalInfoData.academic_achievements || undefined
        };
        success = await updateProfile(updateData);
      } else {
        // Create new profile
        const createData: CreateTeacherProfileData = {
          teacher_code: teacherCode,
          full_name: personalInfoData.full_name,
          nickname: personalInfoData.nickname,
          phone_number: personalInfoData.phone_number,
          teaching_experience: personalInfoData.teaching_experience || undefined,
          academic_achievements: personalInfoData.academic_achievements || undefined
        };
        success = await createProfile(createData);
      }

      setIsSubmitting(false);

      if (success) {
        onComplete();
      }
    }
  };

  const canProceed = stepConfirmed && isPersonalInfoValid;

  return (
    <div className="space-y-6">
      <div className="text-center">
        <Badge variant="outline" className="mb-4">
          ขั้นตอนที่ 1: ยืนยันข้อมูลส่วนตัว
        </Badge>
      </div>

      <PersonalInfoForm
        teacherCode={teacherCode}
        onDataChange={handlePersonalInfoDataChange}
      />

      {/* Confirmation Checkbox */}
      <div className="flex items-center space-x-2 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <Checkbox
          id="step1Confirmed"
          checked={stepConfirmed}
          onCheckedChange={(checked) => setStepConfirmed(checked as boolean)}
        />
        <Label htmlFor="step1Confirmed" className="text-sm font-medium text-blue-800">
          ข้าพเจ้าได้ตรวจสอบข้อมูลส่วนตัวข้างต้นเรียบร้อยแล้ว และยืนยันว่าข้อมูลถูกต้องครบถ้วน *
        </Label>
      </div>

      {/* Navigation */}
      <div className="flex justify-end">
        <Button
          onClick={handleComplete}
          disabled={!canProceed || isSubmitting}
          className="bg-blue-600 hover:bg-blue-700"
        >
          {isSubmitting ? 'กำลังบันทึก...' : 'ไปขั้นตอนถัดไป'}
        </Button>
      </div>
    </div>
  );
}
